<script setup>
import {useUserStore} from '@/stores/user.js';
import {computed, onUnmounted, ref, watch, provide, onMounted} from "vue";
import SortIcon from "@/components/datatable/sortIcon.vue";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import {Icon} from "@iconify/vue";
import {persianDate} from "@/utils/persianDate.js";
import {Button} from "primevue";
import AllButtons from "@/views/orders/Shared/AllButtons.vue";
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import UserPermissions from "@/views/users/Permissions.vue"
import Roles from "@/components/UserProfile/roles.vue"
import AddUser from "@/views/users/AddUser.vue";
import UserProfile from "@/components/UserProfile/index.vue";
import {useCustomerStore} from "@/stores/customer.js";
import {useManagementStore} from "@/stores/management.js";
import {gregorianDate} from "@/utils/gregorianDate.js";

const managementStore = useManagementStore();

const loading = ref(false)
const underReviewAccounts = ref(null)

const filteredUnderReviewAccounts = computed(() => {
  let list = underReviewAccounts.value
  if (keyword.value.trim()) {
    const key = keyword.value.toLowerCase()

    list = list.filter(item => {
      return (
          item.firstName?.toLowerCase().includes(key) ||
          item.lastName?.toLowerCase().includes(key) ||
          item.name?.toLowerCase().includes(key) ||
          item.username?.toLowerCase().includes(key) ||
          item.address?.toLowerCase().includes(key) ||
          String(item.phoneNumber).includes(key)
      )
    })
  }
  return list
})

async function getUnderReviewAccounts(showLoading = true) {
  if (showLoading) {
    loading.value = true;
  }
  const res = await managementStore.action_getUnderReviewAccounts();
  if (res.result === true) {
    underReviewAccounts.value = res.data
  }
  loading.value = false;
}

const userStore = useUserStore();
const customerStore = useCustomerStore();
const isShowUserProfileModal = ref(false);
const keyword = ref('');
const selectedUser = ref(null);
const isShowAllUsers = ref(true)

function showUserProfileModal(row) {
  const user = row?.data || row;
  if (user.isLoading) return;
  userStore.setSelectedUser(user);
  isShowUserProfileModal.value = true
}

onMounted(() => {
  getUnderReviewAccounts(true)
})

provide('isShowUserProfileModal', isShowUserProfileModal)
</script>

<template>
  <div class="h-full">
    <div class="flex flex-col gap-4 w-full h-full">
      <div class="flex flex-col md:flex-row justify-between gap-4 w-full">
        <Breadcrumbs/>
        <div class="flex justify-between gap-3">
          <div
              class="flex flex-row gap-2 items-center w-max h-[32px] bg-slate-900 border border-slate-700 text-slate-300 px-2 rounded-md">
            <span>
              <svg width="16" height="16" viewBox="0 0 24 24">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="1.5"
                      d="m21 21l-4.343-4.343m0 0A8 8 0 1 0 5.343 5.343a8 8 0 0 0 11.314 11.314"/>
              </svg>
            </span>
            <input v-model="keyword" type="text"
                   class="bg-transparent w-full block focus:outline-none h-full rounded-md text-sm">
            <button v-if="keyword"
                    @click="keyword = ''" class="hover:text-red-400">
              <svg width="16" height="16" viewBox="0 0 16 16">
                <path fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="1.5"
                      d="m11.25 4.75l-6.5 6.5m0-6.5l6.5 6.5"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
      <div v-if="loading" class="flex items-center justify-center h-60">
        <Icon icon="eos-icons:bubble-loading" class="size-12"/>
      </div>
      <DataTable v-else
                 :nullSortOrder="-1"
                 :value="filteredUnderReviewAccounts"
                 scrollable
                 scrollHeight="flex"
                 :virtualScrollerOptions="{itemSize: 30}"
                 resizableColumns
                 removableSort
                 stripedRows
                 class="text-xs w-full"
                 size="small"
                 tableStyle="width: 100%; table-layout: fixed;"
                 row-hover
                 @row-click="showUserProfileModal"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300 cursor-pointer'"
      >
        <Column field="id" header="ID" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 2%; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="firstName" header="First Name" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="lastName" header="Last Name" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="name" header="Name" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="username" header="Username" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="phoneNumber" header="Phone Number" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0"
                style="width: 5%; min-width: 100px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="address" header="Address" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 15%; min-width: 46px; height: 30px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="lastUpdate" header="Last Update" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 6%; min-width: 90px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max truncate">
              {{ slotProps.data.lastUpdate ? gregorianDate(slotProps.data.lastUpdate, 'DD MMM HH:mm') : 'ــــ' }}
            </span>
          </template>
        </Column>
        <Column field="registrationTime" header="Registration Time" dataType="numeric" :sortable="true"
                class="overflow-hidden text-ellipsis whitespace-nowrap cursor-pointer"
                style="width: 6%; min-width: 100px; height: 32px">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span class="block w-full max-w-max truncate">
              {{ slotProps.data.lastUpdate ? gregorianDate(slotProps.data.registrationTime, 'DD MMM YYYY') : 'ــــ' }}
            </span>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">
            {{ `Search for the ${isShowAllUsers ? `user's` : `customer's`} name to find them.` }}
          </h1>
        </template>
      </DataTable>
    </div>
    <UserProfile v-if="isShowUserProfileModal" manageKYC managePayment manageVerification
                 :userID="userStore.selectedUser.id" @refresh="getUnderReviewAccounts(false)"/>
  </div>
</template>